import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Option } from '@eds-web/react';
import { setSelectedTaxYear } from '../actions/tax.actions';
import {
    selectSelectedTaxYear,
    selectYearLength,
    selectTaxDocuments
} from '../selectors/tax.selector';
import { TaxDocument } from '../types/payout.types';
import { useTranslation } from 'react-i18next';

interface UseTaxDocumentSelectionReturn {
    selectedTaxYear: number | undefined;
    yearLength: number | undefined;
    taxDocuments: TaxDocument[] | undefined;
    selectOptions: Option<number>[];
    selectDefaultOptions: Option<number>[];
    selectedValue: Option<number> | null;
    hasDocuments: boolean;
    handleYearChange: (year: Option<number>) => void;
    currentYear: number;
}

export const useTaxDocumentSelection = (): UseTaxDocumentSelectionReturn => {
    const dispatch = useDispatch();

    const selectedTaxYear = useSelector(selectSelectedTaxYear);
    const yearLength = useSelector(selectYearLength);
    const taxDocuments = useSelector(selectTaxDocuments);
    const { t } = useTranslation();

    const currentYear = useMemo(() => new Date().getFullYear(), []);

    const selectOptions = useMemo(() => {
        const years = Array.from(
            { length: yearLength || 7 }, // default range = 7
            (_, i) => currentYear - i
        );

        return years.map((year) => ({
            label: year.toString(),
            value: year
        }));
    }, [yearLength, currentYear]);

    const selectDefaultOptions = useMemo(() => {
        const years = Array.from({ length: 1 }, (_, i) => currentYear - i);

        return years.map((year) => ({
            label: t('epic.esport.profile.placeholder.label.year'),
            value: year
        }));
    }, [currentYear]);

    const selectedValue = useMemo(() => {
        if (selectedTaxYear) {
            return {
                label: selectedTaxYear?.toString(),
                value: selectedTaxYear
            };
        }
        return null;
    }, [selectedTaxYear, currentYear]);

    const hasDocuments = useMemo(
        () => Boolean(taxDocuments && taxDocuments.length > 0),
        [taxDocuments]
    );

    const handleYearChange = useCallback(
        (year: Option<number>) => {
            dispatch(setSelectedTaxYear(year.value));
        },
        [dispatch]
    );

    return {
        selectedTaxYear,
        yearLength,
        taxDocuments,
        selectOptions,
        selectedValue,
        hasDocuments,
        handleYearChange,
        currentYear,
        selectDefaultOptions
    };
};
